/**
 * SurvivalEngine.ts
 * Real-time survival simulation engine for American Dream: Pocket Life.
 * 
 * Rules:
 * - Natural Decay: Hunger (-2.5%/min), Energy (-2.0%/min)
 * - Dynamic Coupling: Happiness (-3.0%/min during crisis when Hunger or Energy < 20%, +1.0%/min when both > 70%)
 * - Passive Decor: offsets decay up to caps
 * - Active Buffs: max 3 slots with replacement policy
 * - Penalties: Exhaustion (<=10% Energy), Starvation (<=5% Hunger, doubles energy drain), Depression (<=15% Happiness)
 */

import { EventBus } from './EventBus';
import { SurvivalStats, ActiveBuff, BuffType, PenaltyStatus } from './Types';

export interface SurvivalEngineConfig {
  hungerDecayPerMin?: number;          // Default 2.5% / min
  energyDecayPerMin?: number;          // Default 2.0% / min
  happinessCrisisDrainPerMin?: number; // Default 3.0% / min
  happinessThrivingGainPerMin?: number; // Default 1.0% / min
  maxBuffs?: number;                   // Default 3
  tickIntervalMs?: number;             // Default 1000ms
}

export class SurvivalEngine {
  private stats: SurvivalStats;
  private activeBuffs: ActiveBuff[] = [];
  private passiveDecorEnergyPerMin: number = 0;
  private passiveDecorHappinessPerMin: number = 0;

  private timerId: ReturnType<typeof setInterval> | null = null;
  private lastTickTimestamp: number = Date.now();
  private currentPenalty: PenaltyStatus = 'NONE';

  // Configurable rates per second
  private readonly hungerDecayPerSec: number;
  private readonly energyDecayPerSec: number;
  private readonly happinessCrisisPerSec: number;
  private readonly happinessThrivingPerSec: number;
  private readonly maxBuffs: number;
  private readonly tickIntervalMs: number;

  constructor(initialStats: SurvivalStats, initialBuffs: ActiveBuff[] = [], config: SurvivalEngineConfig = {}) {
    this.stats = initialStats;
    this.activeBuffs = [...initialBuffs];

    const hungerMin = config.hungerDecayPerMin ?? 2.5;
    const energyMin = config.energyDecayPerMin ?? 2.0;
    const happyCrisis = config.happinessCrisisDrainPerMin ?? 3.0;
    const happyThrive = config.happinessThrivingGainPerMin ?? 1.0;

    this.hungerDecayPerSec = hungerMin / 60;
    this.energyDecayPerSec = energyMin / 60;
    this.happinessCrisisPerSec = happyCrisis / 60;
    this.happinessThrivingPerSec = happyThrive / 60;
    this.maxBuffs = config.maxBuffs ?? 3;
    this.tickIntervalMs = config.tickIntervalMs ?? 1000;

    this.evaluatePenalties(false);
  }

  public start(): void {
    if (this.timerId !== null) return;
    this.lastTickTimestamp = Date.now();
    this.timerId = setInterval(() => {
      this.tick();
    }, this.tickIntervalMs);
  }

  public stop(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  public tick(customDeltaMs?: number): void {
    const now = Date.now();
    const rawDeltaMs = customDeltaMs !== undefined ? customDeltaMs : (now - this.lastTickTimestamp);
    this.lastTickTimestamp = now;

    // Safety: ignore negative clock jumps; clamp single active tick to 10s max
    if (rawDeltaMs <= 0) return;
    const deltaMs = Math.min(rawDeltaMs, 10000);
    const deltaSec = deltaMs / 1000;

    // 1. Update Active Buffs and remove expired ones
    this.updateBuffs(now);

    // 2. Hunger calculation: -2.5% per minute
    const hungerDrain = this.hungerDecayPerSec * deltaSec;
    const newHunger = Math.max(0, Math.min(100, this.stats.hunger - hungerDrain));

    // 3. Energy calculation: -2.0% per minute factoring buffs and starvation
    const energyDrainMultiplier = this.getBuffMultiplier('ENERGY_SAVE');
    let effectiveEnergyDecaySec = this.energyDecayPerSec * energyDrainMultiplier;

    // Starvation penalty: doubles energy drain if Hunger <= 5%
    if (newHunger <= 5.0) {
      effectiveEnergyDecaySec *= 2.0;
    }

    const decorEnergySec = (this.passiveDecorEnergyPerMin / 60);
    const energyDelta = (-effectiveEnergyDecaySec + decorEnergySec) * deltaSec;
    const newEnergy = Math.max(0, Math.min(100, this.stats.energy + energyDelta));

    // 4. Happiness Dynamic Coupling:
    // - Crisis: -3.0%/min when Hunger or Energy < 20%
    // - Contentment: +1.0%/min when Hunger > 70% and Energy > 70%
    let happinessRatePerSec = 0;
    if (newHunger < 20.0 || newEnergy < 20.0) {
      happinessRatePerSec = -this.happinessCrisisPerSec;
    } else if (newHunger > 70.0 && newEnergy > 70.0) {
      happinessRatePerSec = this.happinessThrivingPerSec;
    } else {
      happinessRatePerSec = 0;
    }

    const decorHappinessSec = (this.passiveDecorHappinessPerMin / 60);
    const happinessDelta = (happinessRatePerSec + decorHappinessSec) * deltaSec;
    const newHappiness = Math.max(0, Math.min(100, this.stats.happiness + happinessDelta));

    // Commit stats
    this.stats.hunger = Math.round(newHunger * 100) / 100;
    this.stats.energy = Math.round(newEnergy * 100) / 100;
    this.stats.happiness = Math.round(newHappiness * 100) / 100;
    this.stats.lastTickTimestamp = now;

    // Check penalty threshold transitions
    this.evaluatePenalties(true);

    // Emit live stats update
    EventBus.emit('STATS_UPDATED', { stats: { ...this.stats } });
  }

  public updateBuffs(now: number = Date.now()): void {
    const prevCount = this.activeBuffs.length;
    const active: ActiveBuff[] = [];
    const expired: ActiveBuff[] = [];

    for (const buff of this.activeBuffs) {
      if (buff.expiresAt > now) {
        active.push(buff);
      } else {
        expired.push(buff);
      }
    }

    this.activeBuffs = active;

    for (const exp of expired) {
      EventBus.emit('BUFF_EXPIRED', exp);
    }

    if (expired.length > 0 || prevCount !== active.length) {
      EventBus.emit('BUFFS_UPDATED', [...this.activeBuffs]);
    }
  }

  public addBuff(newBuff: ActiveBuff): void {
    // 1. If buff with same ID exists, refresh duration
    const existingIndex = this.activeBuffs.findIndex((b) => b.id === newBuff.id);
    if (existingIndex !== -1) {
      this.activeBuffs[existingIndex] = { ...newBuff };
      EventBus.emit('BUFFS_UPDATED', [...this.activeBuffs]);
      return;
    }

    // 2. If slots are full (3), replace buff with lowest remaining duration
    if (this.activeBuffs.length >= this.maxBuffs) {
      let lowestIndex = 0;
      let earliestExpiry = this.activeBuffs[0].expiresAt;

      for (let i = 1; i < this.activeBuffs.length; i++) {
        if (this.activeBuffs[i].expiresAt < earliestExpiry) {
          earliestExpiry = this.activeBuffs[i].expiresAt;
          lowestIndex = i;
        }
      }

      const replaced = this.activeBuffs[lowestIndex];
      this.activeBuffs.splice(lowestIndex, 1);
      EventBus.emit('BUFF_EXPIRED', replaced);
    }

    // 3. Add new buff
    this.activeBuffs.push({ ...newBuff });
    EventBus.emit('BUFF_ADDED', newBuff);
    EventBus.emit('BUFFS_UPDATED', [...this.activeBuffs]);
  }

  public getBuffMultiplier(type: BuffType): number {
    let mult = 1.0;
    for (const buff of this.activeBuffs) {
      if (buff.type === type) {
        mult *= buff.magnitude;
      }
    }
    return mult;
  }

  public setPassiveDecorRates(energyPerMin: number, happinessPerMin: number): void {
    this.passiveDecorEnergyPerMin = Math.min(3.0, Math.max(0, energyPerMin));
    this.passiveDecorHappinessPerMin = Math.min(5.0, Math.max(0, happinessPerMin));
  }

  public applyStatDelta(deltas: Partial<SurvivalStats>): void {
    if (deltas.money !== undefined && typeof deltas.money === 'number' && !Number.isNaN(deltas.money)) {
      this.stats.money = Math.max(0, this.stats.money + deltas.money);
      EventBus.emit('MONEY_CHANGED', { current: this.stats.money, delta: deltas.money });
    }
    if (deltas.energy !== undefined && typeof deltas.energy === 'number' && !Number.isNaN(deltas.energy)) {
      this.stats.energy = Math.max(0, Math.min(100, this.stats.energy + deltas.energy));
    }
    if (deltas.hunger !== undefined && typeof deltas.hunger === 'number' && !Number.isNaN(deltas.hunger)) {
      this.stats.hunger = Math.max(0, Math.min(100, this.stats.hunger + deltas.hunger));
    }
    if (deltas.happiness !== undefined && typeof deltas.happiness === 'number' && !Number.isNaN(deltas.happiness)) {
      this.stats.happiness = Math.max(0, Math.min(100, this.stats.happiness + deltas.happiness));
    }

    this.evaluatePenalties(true);
    EventBus.emit('STATS_UPDATED', { stats: { ...this.stats }, deltas });
  }

  public getPenalties(): {
    isExhausted: boolean;
    isStarving: boolean;
    isDepressed: boolean;
    canWork: boolean;
    canStudy: boolean;
    speedMultiplier: number;
    payMultiplier: number;
  } {
    const isExhausted = this.stats.energy <= 10.0;
    const isStarving = this.stats.hunger <= 5.0;
    const isDepressed = this.stats.happiness <= 15.0;

    return {
      isExhausted,
      isStarving,
      isDepressed,
      canWork: !isExhausted && !isStarving,
      canStudy: !isExhausted && !isStarving && !isDepressed,
      speedMultiplier: isExhausted ? 0.6 : 1.0,
      payMultiplier: (isDepressed ? 0.5 : 1.0) * this.getBuffMultiplier('PAY_BOOST')
    };
  }

  private evaluatePenalties(notify: boolean): void {
    let newPenalty: PenaltyStatus = 'NONE';

    if (this.stats.hunger <= 5.0) {
      newPenalty = 'STARVING';
    } else if (this.stats.energy <= 10.0) {
      newPenalty = 'EXHAUSTED';
    } else if (this.stats.happiness <= 15.0) {
      newPenalty = 'DEPRESSED';
    }

    if (newPenalty !== this.currentPenalty) {
      this.currentPenalty = newPenalty;
      if (notify && newPenalty !== 'NONE') {
        let message = '';
        if (newPenalty === 'STARVING') {
          message = 'Bạn đang đói lả! Hãy ăn món ăn đặc sản trong tủ lạnh ngay.';
        } else if (newPenalty === 'EXHAUSTED') {
          message = 'Bạn đã kiệt sức! Hãy ngủ trên giường để nạp lại năng lượng.';
        } else if (newPenalty === 'DEPRESSED') {
          message = 'Bạn đang kiệt quệ tinh thần! Hãy thư giãn trên sofa.';
        }
        EventBus.emit('PENALTY_TRIGGERED', { type: newPenalty, message });
      }
    }
  }

  public getStats(): SurvivalStats {
    return { ...this.stats };
  }

  public getActiveBuffs(): ActiveBuff[] {
    return [...this.activeBuffs];
  }
}
